import { ComponentType, registerEvolutionMap } from '@/components/ComponentDefs';

/**
 * Evolution chain definition.
 * Each stage represents a level upgrade for a component type.
 */
export interface EvolutionStage {
  level: number;
  label: string;
  statMultiplier: number;  // multiplied against base stats
  cost: number;            // currency cost to evolve to this level
  costCo?: number;         // Co resource cost (0 = free)
  costElectro?: number;    // Electro resource cost (0 = free)
  // Armor-specific overrides (not scaled by statMultiplier)
  shieldHp?: number;
  ricochetChance?: number;
  ricochetBeamChance?: number;
}

export interface EvolutionChain {
  base: ComponentType;
  stages: EvolutionStage[];
}

/**
 * Evolution chains for upgradeable components.
 * Each chain defines how a component improves at each level.
 * Level 0 = base (no upgrade needed). Levels 1+ require currency.
 *
 * Evolution costs are per-component based on strategic priority:
 *   - Cheap essentials (Structure, Sensor Short) evolve affordably
 *   - Core combat (Cannon Light, Battery) moderately
 *   - Specialist (Flamethrower, Mine Layer) mid-range
 *   - Premium (Heavy Cannon, SAM) expensive
 *   - Elite (Laser, Rocket Pod, Sol-Sol) very expensive
 *
 * Category-specific stat multipliers:
 *   Structural (HP):   1.3 / 1.6 / 1.9
 *   Battery (Energy):  1.5 / 2.0 / 2.5
 *   Sensors (Range):   1.3 / 1.6 / 2.0
 *   Weapons (DPS):     1.25 / 1.5 / 1.8  (lower per-level because DPS compounds with fireRate)
 *   Flamethrower:      1.3 / 1.6 / 2.0   (higher L3 to compensate for 80px range)
 *   Support:           1.3 / 1.6 / 2.0   (combined with independent range/drain scaling)
 *   Movement (Wheel):  1.3 / 1.6         (only HP scales, 2 stages)
 */
export const EVOLUTION_CHAINS: EvolutionChain[] = [
  // ═══════════════════════════════════════════
  //  ESSENTIALS — cheap to evolve, everyone needs them
  //  Co-heavy (physical materials)
  // ═══════════════════════════════════════════
  {
    base: ComponentType.STRUCTURE,
    stages: [
      { level: 1, label: 'Structure+',  statMultiplier: 1.3, cost: 150, costCo: 15 },
      { level: 2, label: 'Structure++', statMultiplier: 1.6, cost: 400, costCo: 35 },
      { level: 3, label: 'Structure³',  statMultiplier: 1.9, cost: 750, costCo: 60 },
    ],
  },
  {
    base: ComponentType.STRUCTURE_TRIANGLE,
    stages: [
      { level: 1, label: 'Bracket+',  statMultiplier: 1.3, cost: 100, costCo: 10 },
      { level: 2, label: 'Bracket++', statMultiplier: 1.6, cost: 300, costCo: 25 },
    ],
  },
  {
    base: ComponentType.WHEEL,
    stages: [
      { level: 1, label: 'Roue+',  statMultiplier: 1.3, cost: 150, costCo: 15 },
      { level: 2, label: 'Roue++', statMultiplier: 1.6, cost: 400, costCo: 35 },
    ],
  },
  {
    base: ComponentType.THRUSTER,
    stages: [
      { level: 1, label: 'Thruster+',  statMultiplier: 1.3, cost: 300, costCo: 20, costElectro: 15 },
      { level: 2, label: 'Thruster++', statMultiplier: 1.6, cost: 700, costCo: 50, costElectro: 35 },
    ],
  },
  {
    base: ComponentType.WING,
    stages: [
      { level: 1, label: 'Ailes+',  statMultiplier: 1.3, cost: 350, costCo: 30, costElectro: 15 },
      { level: 2, label: 'Ailes++', statMultiplier: 1.6, cost: 800, costCo: 60, costElectro: 35 },
    ],
  },
  {
    base: ComponentType.TURBINE,
    stages: [
      { level: 1, label: 'Turbine+',  statMultiplier: 1.3, cost: 400, costCo: 30, costElectro: 25 },
      { level: 2, label: 'Turbine++', statMultiplier: 1.6, cost: 900, costCo: 65, costElectro: 45 },
    ],
  },

  // ═══════════════════════════════════════════
  //  CORE COMBAT — moderate cost, high impact
  //  Electro-heavy (tech upgrades)
  // ═══════════════════════════════════════════
  {
    base: ComponentType.BATTERY,
    stages: [
      { level: 1, label: 'Batterie+',  statMultiplier: 1.5, cost: 200, costElectro: 25 },
      { level: 2, label: 'Batterie++', statMultiplier: 2.0, cost: 500, costElectro: 55 },
      { level: 3, label: 'Batterie³',  statMultiplier: 2.5, cost: 900, costElectro: 90 },
    ],
  },
  {
    base: ComponentType.SENSOR_SHORT,
    stages: [
      { level: 1, label: 'Capteur Court+',  statMultiplier: 1.3, cost: 150, costElectro: 15 },
      { level: 2, label: 'Capteur Court++', statMultiplier: 1.6, cost: 350, costElectro: 35 },
      { level: 3, label: 'Capteur Court³',  statMultiplier: 2.0, cost: 650, costElectro: 60 },
    ],
  },
  {
    base: ComponentType.CANNON_LIGHT,
    stages: [
      { level: 1, label: 'Canon Leger+',  statMultiplier: 1.25, cost: 200, costCo: 15, costElectro: 10 },
      { level: 2, label: 'Canon Leger++', statMultiplier: 1.5,  cost: 450, costCo: 35, costElectro: 25 },
      { level: 3, label: 'Canon Leger³',  statMultiplier: 1.8,  cost: 800, costCo: 60, costElectro: 45 },
    ],
  },

  // ═══════════════════════════════════════════
  //  SPECIALIST — mid-range cost, niche roles
  //  Mixed Co + Electro
  // ═══════════════════════════════════════════
  {
    base: ComponentType.ARMOR,
    stages: [
      { level: 1, label: 'Blindage+',  statMultiplier: 1.3, cost: 250,  costCo: 25, shieldHp: 60, ricochetChance: 0.25 },
      { level: 2, label: 'Blindage++', statMultiplier: 1.6, cost: 550,  costCo: 55, shieldHp: 80, ricochetChance: 0.50 },
      { level: 3, label: 'Blindage³',  statMultiplier: 1.9, cost: 1000, costCo: 90, shieldHp: 100, ricochetChance: 0.50, ricochetBeamChance: 0.50 },
    ],
  },
  {
    base: ComponentType.STAB_LEGS,
    stages: [
      { level: 1, label: 'Stab. Legs+',  statMultiplier: 1.3, cost: 200, costCo: 15 },
      { level: 2, label: 'Stab. Legs++', statMultiplier: 1.6, cost: 500, costCo: 35 },
    ],
  },
  {
    base: ComponentType.FLAMETHROWER,
    stages: [
      { level: 1, label: 'Lance-Flammes+',  statMultiplier: 1.3, cost: 200, costCo: 15, costElectro: 15 },
      { level: 2, label: 'Lance-Flammes++', statMultiplier: 1.6, cost: 500, costCo: 35, costElectro: 30 },
      { level: 3, label: 'Lance-Flammes³',  statMultiplier: 2.0, cost: 900, costCo: 60, costElectro: 55 },
    ],
  },
  {
    base: ComponentType.MINE_LAYER,
    stages: [
      { level: 1, label: 'Mine Layer+',  statMultiplier: 1.3, cost: 200, costCo: 15, costElectro: 10 },
      { level: 2, label: 'Mine Layer++', statMultiplier: 1.6, cost: 450, costCo: 35, costElectro: 25 },
      { level: 3, label: 'Mine Layer³',  statMultiplier: 2.0, cost: 800, costCo: 60, costElectro: 45 },
    ],
  },
  {
    base: ComponentType.JUMPER,
    stages: [
      { level: 1, label: 'Jumper+',  statMultiplier: 1.3, cost: 200, costCo: 15, costElectro: 10 },
      { level: 2, label: 'Jumper++', statMultiplier: 1.6, cost: 450, costCo: 35, costElectro: 25 },
      { level: 3, label: 'Jumper³',  statMultiplier: 2.0, cost: 800, costCo: 60, costElectro: 45 },
    ],
  },
  {
    base: ComponentType.REPAIR_MODULE,
    stages: [
      { level: 1, label: 'Reparation+',  statMultiplier: 1.3, cost: 250, costCo: 10, costElectro: 15 },
      { level: 2, label: 'Reparation++', statMultiplier: 1.6, cost: 550, costCo: 25, costElectro: 35 },
      { level: 3, label: 'Reparation³',  statMultiplier: 2.0, cost: 950, costCo: 45, costElectro: 60 },
    ],
  },
  {
    base: ComponentType.RECHARGE_MODULE,
    stages: [
      { level: 1, label: 'Recharge+',  statMultiplier: 1.3, cost: 250, costElectro: 25 },
      { level: 2, label: 'Recharge++', statMultiplier: 1.6, cost: 550, costElectro: 55 },
      { level: 3, label: 'Recharge³',  statMultiplier: 2.0, cost: 950, costElectro: 85 },
    ],
  },
  {
    base: ComponentType.BURST_CANNON,
    stages: [
      { level: 1, label: 'Rafale+',  statMultiplier: 1.25, cost: 300, costCo: 20, costElectro: 15 },
      { level: 2, label: 'Rafale++', statMultiplier: 1.5,  cost: 650, costCo: 45, costElectro: 35 },
      { level: 3, label: 'Rafale³',  statMultiplier: 1.8,  cost: 1100, costCo: 75, costElectro: 60 },
    ],
  },

  // ═══════════════════════════════════════════
  //  PREMIUM — expensive, powerful upgrades
  // ═══════════════════════════════════════════
  {
    base: ComponentType.MISSILE_SAM,
    stages: [
      { level: 1, label: 'AA Missile+',  statMultiplier: 1.25, cost: 300, costCo: 25, costElectro: 25 },
      { level: 2, label: 'AA Missile++', statMultiplier: 1.5,  cost: 700, costCo: 55, costElectro: 45 },
      { level: 3, label: 'AA Missile³',  statMultiplier: 1.8,  cost: 1200, costCo: 85, costElectro: 75 },
    ],
  },
  {
    base: ComponentType.CANNON_HEAVY,
    stages: [
      { level: 1, label: 'Canon Lourd+',  statMultiplier: 1.25, cost: 350, costCo: 30, costElectro: 15 },
      { level: 2, label: 'Canon Lourd++', statMultiplier: 1.5,  cost: 750, costCo: 60, costElectro: 35 },
      { level: 3, label: 'Canon Lourd³',  statMultiplier: 1.8,  cost: 1300, costCo: 95, costElectro: 65 },
    ],
  },
  {
    base: ComponentType.SENSOR_MEDIUM,
    stages: [
      { level: 1, label: 'Radar+',  statMultiplier: 1.3, cost: 300, costElectro: 30 },
      { level: 2, label: 'Radar++', statMultiplier: 1.6, cost: 650, costElectro: 60 },
      { level: 3, label: 'Radar³',  statMultiplier: 2.0, cost: 1100, costElectro: 95 },
    ],
  },

  // ═══════════════════════════════════════════
  //  ELITE — very expensive, endgame
  // ═══════════════════════════════════════════
  {
    base: ComponentType.LASER,
    stages: [
      { level: 1, label: 'Laser+',  statMultiplier: 1.25, cost: 500, costElectro: 45 },
      { level: 2, label: 'Laser++', statMultiplier: 1.5,  cost: 1000, costElectro: 85 },
      { level: 3, label: 'Laser³',  statMultiplier: 1.8,  cost: 1600, costElectro: 120 },
    ],
  },
  {
    base: ComponentType.MISSILE_SOL_SOL,
    stages: [
      { level: 1, label: 'Sol-Sol+',  statMultiplier: 1.25, cost: 450, costCo: 30, costElectro: 30 },
      { level: 2, label: 'Sol-Sol++', statMultiplier: 1.5,  cost: 900, costCo: 65, costElectro: 60 },
      { level: 3, label: 'Sol-Sol³',  statMultiplier: 1.8,  cost: 1500, costCo: 105, costElectro: 95 },
    ],
  },
  {
    base: ComponentType.ROCKET_POD,
    stages: [
      { level: 1, label: 'Rocket Pod+',  statMultiplier: 1.25, cost: 500, costCo: 35, costElectro: 35 },
      { level: 2, label: 'Rocket Pod++', statMultiplier: 1.5,  cost: 1100, costCo: 75, costElectro: 65 },
      { level: 3, label: 'Rocket Pod³',  statMultiplier: 1.8,  cost: 1800, costCo: 115, costElectro: 105 },
    ],
  },
  // Explosive Charge — kamikaze evolution
  // Lv1: +stun survivors (2s)  |  Lv2: +incendiary (fire DOT)  |  Lv3: NUKE animation + knockback
  {
    base: ComponentType.EXPLOSIVE_CHARGE,
    stages: [
      { level: 1, label: 'Charge+',  statMultiplier: 1.4, cost: 200, costCo: 25, costElectro: 10 },
      { level: 2, label: 'Charge++', statMultiplier: 1.8, cost: 500, costCo: 55, costElectro: 25 },
      { level: 3, label: 'NUKE',     statMultiplier: 2.5, cost: 1200, costCo: 90, costElectro: 75 },
    ],
  },

  // ═══════════════════════════════════════════
  //  UTILITY — cheap, economic components
  // ═══════════════════════════════════════════
  {
    base: ComponentType.EXCAVATOR,
    stages: [
      { level: 1, label: 'Excavator+',  statMultiplier: 1.3, cost: 100, costCo: 10 },
      { level: 2, label: 'Excavator++', statMultiplier: 1.6, cost: 250, costCo: 25 },
      { level: 3, label: 'Excavator³',  statMultiplier: 2.0, cost: 450, costCo: 45 },
    ],
  },
];

/** Quick lookup: component type → evolution chain */
export const EVOLUTION_MAP = new Map<ComponentType, EvolutionChain>(
  EVOLUTION_CHAINS.map(c => [c.base, c]),
);

/** Get resource costs for evolving from currentLevel to currentLevel+1 */
export function getEvolutionResourceCost(type: ComponentType, currentLevel: number): { co: number; electro: number } {
  const chain = EVOLUTION_MAP.get(type);
  if (!chain) return { co: 0, electro: 0 };
  const stage = chain.stages.find(s => s.level === currentLevel + 1);
  if (!stage) return { co: 0, electro: 0 };
  return { co: stage.costCo ?? 0, electro: stage.costElectro ?? 0 };
}

// Register with ComponentDefs so getEvolutionCost() can look up chain costs
// without circular imports (ComponentDefs → ComponentEvolution → ComponentDefs)
registerEvolutionMap(EVOLUTION_MAP);
